<template>
  <div class="linked-professionals">
    <div class="columns">
      <div class="column col-12">
        <button class="btn btn-primary btn-sm mb-2"
                @click="openAddModal"
                :disabled="!collaboratorId">
          Adicionar profissional
        </button>
      </div>
    </div>
    <div class="loading mt-2" v-if="loading && linkedProfessionals.length === 0" />
    <template v-else>
      <div class="empty mt-2" v-if="linkedProfessionals.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
        </div>
        <p class="empty-title h5">Profissionais</p>
        <p class="empty-subtitle">
          Nenhum profissional vinculado. Clique no botão acima para adicionar.
        </p>
      </div>
      <table class="table table-striped table-hover" v-else>
        <thead>
          <tr>
            <th class="hide-sm" width="40px">#</th>
            <th>Nome</th>
            <th>Especialidade</th>
            <th class="text-right" width="80px">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(professional, i) in linkedProfessionals" :key="professional.id">
            <td class="hide-sm">{{ i + 1 }}</td>
            <td>{{ professional.name }}</td>
            <td>
              <span v-if="professional.specialtyNames && professional.specialtyNames.length > 0">
                {{ professional.specialtyNames[0] }}
                <span
                  v-if="professional.specialtyNames.length > 1"
                  class="label text-primary tooltip hover-link ml-1"
                  :data-tooltip="professional.specialtyNames.slice(1).join('\n')">
                  +{{ professional.specialtyNames.length - 1 }}
                </span>
              </span>
            </td>
            <td class="text-right">
              <button class="btn btn-sm btn-action btn-icon btn-error tooltip"
                      data-tooltip="Remover"
                      :disabled="removing"
                      :class="{loading: removing && removingIndex === i}"
                      @click="confirmRemove(professional, i)">
                <fa-icon :icon="['fal', 'times']"/>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <dx-modal title="Adicionar profissional"
              v-model="addModal.show"
              id="modal-add-professional">
      <div class="columns">
        <div class="column col-12 form-group">
          <label class="form-label">Buscar profissional</label>
          <dx-autocomplete
            v-model="selectedProfessional"
            :source="findProfessional"
            label="name"
            track-by="id"
            :debounce="800"
            @select="addProfessional"
            placeholder="Informe o nome do profissional..."
            input-id="professional-search">
            <template v-slot:action>
              <button
                class="btn btn-action input-group-btn btn-icon btn-neutral"
                tabindex="-1">
                <fa-icon :icon="['fal', 'search']"/>
              </button>
            </template>
            <template v-slot="{ item }">
              <a>
                <span>{{ item.name }}</span>
                <span v-if="item.council" class="ml-1">
                  ({{ item.council.name }}-{{ item.council.state }} {{ item.council.record }})
                </span>
              </a>
            </template>
          </dx-autocomplete>
        </div>
      </div>
      <div class="empty mt-2" v-if="filteredProfessionals.length === 0 && !addModal.loading">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
        </div>
        <p class="empty-title h6">Profissionais</p>
        <p class="empty-subtitle">
          Nenhum profissional disponível para vincular
        </p>
      </div>
      <table
        class="table table-striped table-hover c-hand"
        v-else-if="filteredProfessionals.length > 0">
        <thead>
          <tr>
            <th>#</th>
            <th>Nome</th>
            <th>Especialidade</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(professional, i) in filteredProfessionals"
              :key="professional.id"
              @click="addProfessional(professional)">
            <td>{{ i + 1 }}</td>
            <td>{{ professional.name }}</td>
            <td>
              <span v-if="professional.specialtyNames && professional.specialtyNames.length > 0">
                {{ professional.specialtyNames[0] }}
                <span
                  v-if="professional.specialtyNames.length > 1"
                  class="label text-primary tooltip hover-link ml-1"
                  :data-tooltip="professional.specialtyNames.slice(1).join('\n')">
                  +{{ professional.specialtyNames.length - 1 }}
                </span>
              </span>
            </td>
            <td class="text-right">
              <fa-icon :icon="['fal', 'chevron-right']"/>
            </td>
          </tr>
        </tbody>
      </table>
      <template v-slot:footer>
        <button class="btn" @click="closeAddModal">Sair</button>
      </template>
    </dx-modal>
  </div>
</template>

<script>
import * as professionalApi from '@/app/professional/api';

export default {
  props: {
    collaboratorId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      removing: false,
      removingIndex: -1,
      linkedProfessionals: [],
      allProfessionals: [],
      selectedProfessional: null,
      addModal: {
        show: false,
        loading: false,
      },
    };
  },
  computed: {
    filteredProfessionals() {
      return this.allProfessionals
        .filter(professional => !this.linkedProfessionals
          .some(linked => linked.id === professional.id));
    },
  },
  watch: {
    collaboratorId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.loadLinkedProfessionals();
        } else {
          this.linkedProfessionals = [];
        }
      },
    },
  },
  methods: {
    async loadLinkedProfessionals() {
      if (!this.collaboratorId) {
        return;
      }
      this.loading = true;
      try {
        const id = this.collaboratorId;
        const { data } = await this.$http.get(`/collaborators/${id}/linked-professionals`);

        this.linkedProfessionals = data.items.map((item) => {
          const specialtyNames = item.specialties?.map(s => s.name) || [];
          return {
            ...item,
            specialtyNames,
          };
        });
      } catch (error) {
        this.$toast.error(error);
      } finally {
        this.loading = false;
      }
    },
    async loadAllProfessionals() {
      this.addModal.loading = true;
      try {
        const data = await professionalApi.allActive(true);
        this.allProfessionals = (data.items || []).map((item) => {
          const professional = item.professional || item;
          const specialtyNames = professional.specialtyNames
            || (professional.specialties
              ? professional.specialties.map(s => s.name)
              : []);
          return {
            ...item,
            specialtyNames,
          };
        });
      } catch (error) {
        this.$toast.error('Erro ao carregar profissionais');
      } finally {
        this.addModal.loading = false;
      }
    },
    async findProfessional(search) {
      try {
        const params = {
          search,
          limit: 20,
          active: true,
        };
        const { items } = await professionalApi.index(params);
        return items.map((item) => {
          const professional = item.professional || item;
          const specialtyNames = professional.specialtyNames
            || (professional.specialties
              ? professional.specialties.map(s => s.name)
              : []);
          return {
            ...item,
            specialtyNames,
          };
        });
      } catch (error) {
        this.$toast.error('Erro ao buscar profissionais');
        return [];
      }
    },
    openAddModal() {
      if (this.allProfessionals.length === 0) {
        this.loadAllProfessionals();
      }
      this.selectedProfessional = null;
      this.addModal.show = true;
    },
    closeAddModal() {
      this.addModal.show = false;
      this.selectedProfessional = null;
    },
    async addProfessional(professional) {
      if (!professional || !professional.id) {
        return;
      }
      if (this.linkedProfessionals.some(p => p.id === professional.id)) {
        this.$toast.show('Este profissional já está vinculado', { type: 'warning' });
        return;
      }
      try {
        const { data } = await this.$http.post(this.buildProfessionalPath(professional.id));
        data.specialtyNames = data.specialties?.map(s => s.name) || [];
        this.linkedProfessionals.unshift(data);
        this.closeAddModal();
        this.$toast.show('Profissional vinculado com sucesso');
      } catch (error) {
        this.$toast.error(error);
      }
    },
    confirmRemove(professional, index) {
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente desvincular este profissional?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          },
          {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              close();
              this.removeProfessional(professional.id, index);
            },
          },
        ],
      });
    },
    async removeProfessional(professionalId, index) {
      if (!this.collaboratorId) {
        return;
      }
      this.removing = true;
      this.removingIndex = index;
      try {
        await this.$http.delete(this.buildProfessionalPath(professionalId));
        this.linkedProfessionals.splice(index, 1);
        this.$toast.show('Profissional desvinculado com sucesso');
      } catch (error) {
        this.$toast.error(error);
      } finally {
        this.removing = false;
        this.removingIndex = -1;
      }
    },
    buildProfessionalPath(professionalId) {
      return `/collaborators/${this.collaboratorId}/linked-professionals/${professionalId}`;
    },
  },
};
</script>

<style lang="scss">
  @import "./src/assets/scss/_variables.scss";
  .linked-professionals {
    margin-top: 16px;
    .empty {
      background-color: $light-color;
      padding: $layout-spacing-lg;
    }
  }
</style>
